// ─────────────────────────────────────────────────────────────
// OutputTab — execution results display.
// Layout: [LeftSidebar (file list)] | [Viewer + ConsolePanel] | [AiPanel]
//
// Viewer is type-aware: images (zoom/pan), PDFs (iframe), CSVs (table),
// XLSX/other (download card), text/JSON/MD (pre block).
// ─────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChatMessage } from "./types";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { AiPanel } from "./AiPanel";
import { ConsolePanel } from "./ConsolePanel";
import { FullscreenImageModal } from "./modals";
import { executeApi, imageApi } from "../../api/client";
import type { ExecutionResult, ExecutionOutputFile, ExecutionOutputFileType, ImageOverlay, OverlayShape } from "../../api/client";

// ── Layout constants ─────────────────────────────────────────

const LEFT_SIDEBAR_WIDTH = 220;
const CSV_ROW_CAP = 500;

// ── Local types ───────────────────────────────────────────────

interface OutputFile {
  name: string;
  url: string;
  type: ExecutionOutputFileType;
  size_bytes?: number;
  /** True for the original input image entry (fetched differently from output files). */
  isInput?: boolean;
  inputImageId?: number;
  inputImageFile?: File;
  overlay?: ImageOverlay;
}

const DEFAULT_OVERLAY_STROKE = "#00e5ff";
const DEFAULT_OVERLAY_STROKE_WIDTH = 2;

function shapeStroke(s: OverlayShape): string {
  if (s.type === "point") return s.color ?? DEFAULT_OVERLAY_STROKE;
  return s.stroke ?? DEFAULT_OVERLAY_STROKE;
}

interface OutputTabProps {
  containerWidth: number;
  /** The most recent execution result. Null = no run yet. */
  result: ExecutionResult | null;
  /** Library image ID that was used as input — shown first in the file list. */
  inputImageId?: number;
  /** Local file that was used as input — shown first in the file list. */
  inputImageFile?: File;
  /** Shared chat history — lifted to ScriptHelperApp so it persists across tab switches. */
  chatMessages: ChatMessage[];
  onChatMessagesChange: (msgs: ChatMessage[]) => void;
  /** Called when the user requests a re-run from the Output tab AI panel. */
  onRun?: () => void;
  /** True while a script execution is in-flight. Forwarded to AiPanel so its
   *  Run Script chips show the spinner + "Running…" label like in PythonTab. */
  isRunning?: boolean;
  /**
   * Auto-error-analysis trigger (Greg, 2026-06-05). When a run fails while the
   * user is already on the Output tab, the parent routes the failure prompt
   * here instead of force-switching to the editor. Forwarded to the embedded
   * AiPanel, which consumes it once and clears via onAutoAnalyzeErrorConsumed.
   * Null when there's nothing to analyse. Parent guarantees exactly one
   * AiPanel (this one OR a PythonTab one) ever receives a non-null value, so
   * the analysis fires exactly once.
   */
  autoAnalyzeError?: string | null;
  onAutoAnalyzeErrorConsumed?: () => void;
  /**
   * Returns the current Monaco editor contents at call time. Forwarded to the
   * AI panel so Marie always sees the script the user has open, even when the
   * user is chatting from the Output tab.
   */
  getEditorCode?: () => string;
  /** Called when Marie returns a code block and we need to update the Python editor. */
  onInjectCode?: (code: string) => void;
  /**
   * Called when the user restores an earlier in-session Marie snapshot
   * via the "Restore" chip. Same shape as onInjectCode — parent should
   * land the code in the editor without persisting anything.
   */
  onRestoreCode?: (code: string) => void;
  /**
   * Marie session capture (Sam, 2026-05-06). Threaded through so the AI panel
   * hosted here can keep contributing to / capturing the session id. The
   * user-facing Download Session button was removed 2026-05-07 — session
   * export now lives only in the admin Sessions tab.
   */
  marieSessionId?: number | null;
  onMarieSessionIdChange?: (id: number | null) => void;
  newSessionArmKey?: number;
  onArmNewSession?: () => void;
  /**
   * Forwarded to AiPanel — called by "Start new chat" so the parent can
   * trigger a full workspace reset (editor + image + scriptId + chat +
   * session) on top of the local chat reset AiPanel already does.
   */
  onClearWorkspace?: () => void;
  /**
   * Per-user Marie model allowlist (Sam, 2026-05-14). Forwarded to the
   * embedded AiPanel — same filtering as the PythonTab one.
   */
  allowedModels?: string[] | null;
  /** Tier-limit toast hook forwarded to AiPanel. */
  onTierLimitToast?: (info: {
    kind: "model_not_allowed" | "quota_exceeded_marie_chat";
    message: string;
  }) => void;
  /**
   * Lifted Marie model selection (Greg, 2026-05-15). Owned by
   * ScriptHelperApp so this AiPanel shares one value with both PythonTab
   * panels. The picker no longer resets when handleRun auto-switches the
   * active tab over to Output.
   */
  selectedMarieModel: string;
  onSelectedMarieModelChange: (modelId: string) => void;
}

// ── Helpers ──────────────────────────────────────────────────

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function iconForType(type: ExecutionOutputFileType) {
  switch (type) {
    case "image":
      return ImageOutlinedIcon;
    case "pdf":
      return PictureAsPdfOutlinedIcon;
    case "csv":
      return TableChartOutlinedIcon;
    case "xlsx":
      return DescriptionOutlinedIcon;
    case "text":
      return NotesOutlinedIcon;
    default:
      return InsertDriveFileOutlinedIcon;
  }
}

/**
 * Minimal RFC-4180-ish CSV parser. Handles quoted fields containing
 * commas, embedded newlines, and escaped double quotes ("").
 * Not a complete implementation but adequate for typical script output.
 */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === ",") {
      row.push(field);
      field = "";
      i++;
      continue;
    }
    if (c === "\r") {
      // handle CRLF — consume the \n if present on next iter
      i++;
      continue;
    }
    if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      i++;
      continue;
    }
    field += c;
    i++;
  }
  // Trailing field / row
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Drop trailing empty rows
  while (rows.length > 0) {
    const last = rows[rows.length - 1];
    if (last.length === 1 && last[0] === "") rows.pop();
    else break;
  }
  return rows;
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on next tick so the browser has time to initiate the download
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function OutputTab({
  containerWidth,
  result,
  inputImageId,
  inputImageFile,
  chatMessages,
  onChatMessagesChange,
  onRun,
  isRunning,
  autoAnalyzeError,
  onAutoAnalyzeErrorConsumed,
  getEditorCode,
  onInjectCode,
  onRestoreCode,
  marieSessionId,
  onMarieSessionIdChange,
  newSessionArmKey,
  onArmNewSession,
  onClearWorkspace,
  allowedModels,
  onTierLimitToast,
  selectedMarieModel,
  onSelectedMarieModelChange,
}: OutputTabProps) {
  // Build the unified file list from output_files (new) with a legacy fallback for output_images.
  const outputEntries: OutputFile[] = useMemo(() => {
    const list: OutputFile[] = [];

    // Input entry
    if (inputImageId != null) {
      list.push({
        name: "Input Image",
        url: `__input__/${inputImageId}`,
        type: "image",
        isInput: true,
        inputImageId,
      });
    } else if (inputImageFile) {
      list.push({
        name: "Input Image",
        url: "__input__/file",
        type: "image",
        isInput: true,
        inputImageFile,
      });
    }

    // New output_files contract
    const files: ExecutionOutputFile[] | undefined = result?.output_files;
    if (files && files.length > 0) {
      for (const f of files) {
        list.push({ name: f.name, url: f.url, type: f.type, size_bytes: f.size_bytes, overlay: f.overlay });
      }
    } else if (result?.output_images) {
      // Legacy fallback (only hit when backend hasn't migrated — Sam says it's still served for compat).
      for (const img of result.output_images) {
        list.push({ name: img.name, url: img.url, type: "image" });
      }
    }

    return list;
  }, [result, inputImageId, inputImageFile]);

  const [selectedFile, setSelectedFile] = useState<OutputFile | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(true);

  // Viewer content state — reused across file types
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [csvRows, setCsvRows] = useState<string[][] | null>(null);
  const [csvRawText, setCsvRawText] = useState<string | null>(null);
  const [showCsvRaw, setShowCsvRaw] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);

  // Blob URL cache keyed by file.url — avoids re-fetching when toggling between files.
  // Cleared on new result (new run) and on unmount. Stores raw blob URLs for image/pdf.
  const blobCache = useRef<Map<string, string>>(new Map());

  // ── Image viewer zoom / pan ──────────────────────────────────
  const [zoom, setZoom] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);
  const panStartRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  const MIN_ZOOM = 0.25;
  const MAX_ZOOM = 10;

  function resetView() {
    setZoom(1);
    setTranslateX(0);
    setTranslateY(0);
  }

  const handleViewerWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const container = viewerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    setZoom((prevZoom) => {
      const delta = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prevZoom * delta));
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      setTranslateX((prevTx) => {
        const imgX = (cursorX - cx - prevTx) / prevZoom;
        const next = cursorX - cx - imgX * nextZoom;
        return nextZoom <= 1 ? 0 : next;
      });
      setTranslateY((prevTy) => {
        const imgY = (cursorY - cy - prevTy) / prevZoom;
        const next = cursorY - cy - imgY * nextZoom;
        return nextZoom <= 1 ? 0 : next;
      });
      return nextZoom;
    });
  }, []);

  function handleViewerMouseDown(e: React.MouseEvent) {
    if (e.button !== 0) return;
    e.preventDefault();
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, tx: translateX, ty: translateY };
  }

  function handleViewerMouseMove(e: React.MouseEvent) {
    if (!isPanning || !panStartRef.current) return;
    setTranslateX(panStartRef.current.tx + (e.clientX - panStartRef.current.x));
    setTranslateY(panStartRef.current.ty + (e.clientY - panStartRef.current.y));
  }

  function handleViewerMouseUp() {
    setIsPanning(false);
    panStartRef.current = null;
  }

  // Reset selected file and view when a new result arrives — default to first output (not input).
  // ConsolePanel handles its own auto-expand-on-failure / collapse-on-success behavior.
  useEffect(() => {
    // New run — revoke and clear all cached blob URLs from the previous run
    blobCache.current.forEach((url) => URL.revokeObjectURL(url));
    blobCache.current.clear();

    const firstOutput = outputEntries.find((f) => !f.isInput) ?? outputEntries[0] ?? null;
    setSelectedFile(firstOutput);
    setImageUrl(null);
    setPdfUrl(null);
    setTextContent(null);
    setCsvRows(null);
    setCsvRawText(null);
    setShowCsvRaw(false);
    setViewerError(null);
    resetView();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  // Reset image pan/zoom when switching files
  useEffect(() => {
    resetView();
    setShowCsvRaw(false);
  }, [selectedFile?.url]);

  // Load the selected file's content based on type.
  useEffect(() => {
    if (!selectedFile) {
      setImageUrl(null);
      setPdfUrl(null);
      setTextContent(null);
      setCsvRows(null);
      setCsvRawText(null);
      return;
    }

    let cancelled = false;
    setViewerLoading(true);
    setViewerError(null);
    setImageUrl(null);
    setPdfUrl(null);
    setTextContent(null);
    setCsvRows(null);
    setCsvRawText(null);

    const done = () => { if (!cancelled) setViewerLoading(false); };
    const fail = (err: unknown) => {
      if (cancelled) return;
      setViewerError(err instanceof Error ? err.message : "Failed to load file");
      setViewerLoading(false);
    };

    // Input image — special-case via imageApi / local File
    if (selectedFile.isInput) {
      const cached = blobCache.current.get(selectedFile.url);
      if (cached) {
        setImageUrl(cached);
        done();
        return () => { cancelled = true; };
      }

      if (selectedFile.inputImageId != null) {
        imageApi.getImageUrl(selectedFile.inputImageId).then((url) => {
          if (cancelled) { URL.revokeObjectURL(url); return; }
          blobCache.current.set(selectedFile.url, url);
          setImageUrl(url);
          done();
        }).catch(fail);
      } else if (selectedFile.inputImageFile) {
        const url = URL.createObjectURL(selectedFile.inputImageFile);
        blobCache.current.set(selectedFile.url, url);
        setImageUrl(url);
        done();
      } else {
        done();
      }
      return () => { cancelled = true; };
    }

    // Output file — dispatch on type
    switch (selectedFile.type) {
      case "image": {
        const cached = blobCache.current.get(selectedFile.url);
        if (cached) { setImageUrl(cached); done(); break; }
        executeApi.getOutputImageUrl(selectedFile.url).then((url) => {
          if (cancelled) { URL.revokeObjectURL(url); return; }
          blobCache.current.set(selectedFile.url, url);
          setImageUrl(url);
          done();
        }).catch(fail);
        break;
      }
      case "pdf": {
        const cached = blobCache.current.get(selectedFile.url);
        if (cached) { setPdfUrl(cached); done(); break; }
        executeApi.getOutputFileBlob(selectedFile.url).then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          blobCache.current.set(selectedFile.url, url);
          setPdfUrl(url);
          done();
        }).catch(fail);
        break;
      }
      case "csv": {
        executeApi.getOutputFileText(selectedFile.url).then((txt) => {
          if (cancelled) return;
          setCsvRawText(txt);
          try {
            setCsvRows(parseCsv(txt));
          } catch (err) {
            fail(err);
            return;
          }
          done();
        }).catch(fail);
        break;
      }
      case "text": {
        executeApi.getOutputFileText(selectedFile.url).then((txt) => {
          if (cancelled) return;
          // Pretty-print JSON when possible
          if (selectedFile.name.toLowerCase().endsWith(".json")) {
            try {
              const parsed = JSON.parse(txt);
              setTextContent(JSON.stringify(parsed, null, 2));
            } catch {
              setTextContent(txt);
            }
          } else {
            setTextContent(txt);
          }
          done();
        }).catch(fail);
        break;
      }
      case "xlsx":
      case "other":
      default:
        // No content to fetch — the placeholder handles its own download on demand.
        done();
        break;
    }

    return () => { cancelled = true; };
  }, [selectedFile]);

  // Revoke all cached blob URLs on unmount
  useEffect(() => {
    return () => {
      blobCache.current.forEach((url) => URL.revokeObjectURL(url));
      blobCache.current.clear();
    };
  }, []);

  // ── Pre-warm: eagerly convert + cache the run's images in the background ──
  //
  // Greg's core Output-tab workflow is toggling between the input image and
  // the script's output image. The backend converts TIFF→PNG on first request
  // and caches it (server-side + a 1h browser cache), so every toggle AFTER
  // the first is instant — but the FIRST view of each image pays a cold
  // conversion. To make the first toggle instant too, we pre-fetch the input
  // image and all output image(s) as soon as the run's results land, before
  // the user clicks anything. Each pre-fetch hits the exact same URL the
  // toggle would, populating both the server cache and this component's
  // blobCache — so a pre-warmed image is a true zero-flicker cache hit.
  //
  // Constraints honored here:
  //  - Gated once per job_id (prewarmedJobRef) so a re-render never re-storms.
  //  - Sequential, single-flight — one conversion at a time, never a burst
  //    (starter #8 has 4 outputs + input; a parallel fan-out would hammer
  //    the backend's TIFF converter).
  //  - Priority order: input image first (it's half the toggle), then the
  //    remaining output images. The currently-shown image is already loading
  //    via the selected-file effect, so it's skipped.
  //  - Non-blocking and silent — failures just mean that image converts on
  //    click as it does today; no error UI, no state change to the viewer.
  //  - Skips entries already in blobCache or already pre-warmed in flight.
  const prewarmedJobRef = useRef<string | null>(null);

  useEffect(() => {
    const jobId = result?.job_id;
    if (!jobId) return;
    // Gate: only pre-warm a given run once, even across re-renders.
    if (prewarmedJobRef.current === jobId) return;
    prewarmedJobRef.current = jobId;

    let cancelled = false;
    const inFlight = new Set<string>();

    // Image entries only — TIFF→PNG conversion is the slow path we're warming.
    // Input image goes first (it's the other half of Greg's toggle), then the
    // remaining outputs in list order.
    const imageEntries = outputEntries.filter((f) => f.type === "image");
    const ordered = [
      ...imageEntries.filter((f) => f.isInput),
      ...imageEntries.filter((f) => !f.isInput),
    ];

    async function warmOne(file: OutputFile): Promise<void> {
      // Skip anything already cached (e.g. the currently-shown image once its
      // selected-file effect resolves) or already being pre-warmed.
      if (blobCache.current.has(file.url) || inFlight.has(file.url)) return;
      inFlight.add(file.url);
      try {
        let url: string | null = null;
        if (file.isInput) {
          if (file.inputImageId != null) {
            url = await imageApi.getImageUrl(file.inputImageId);
          } else if (file.inputImageFile) {
            // Local file — no backend conversion needed, but cache it anyway
            // so the toggle is a consistent zero-flicker hit.
            url = URL.createObjectURL(file.inputImageFile);
          }
        } else {
          url = await executeApi.getOutputImageUrl(file.url);
        }
        if (url == null) return;
        // The selected-file effect may have resolved and cached this URL while
        // we were fetching — if so, drop ours to avoid leaking a blob URL.
        if (cancelled || blobCache.current.has(file.url)) {
          URL.revokeObjectURL(url);
          return;
        }
        blobCache.current.set(file.url, url);
      } catch {
        // Silent — a failed pre-warm just means this image converts on click,
        // exactly as it did before this optimization.
      } finally {
        inFlight.delete(file.url);
      }
    }

    // Sequential single-flight: one conversion at a time, in priority order.
    (async () => {
      for (const file of ordered) {
        if (cancelled) return;
        await warmOne(file);
      }
    })();

    return () => { cancelled = true; };
  // outputEntries is derived from `result` (+ stable input props); keying on
  // job_id via prewarmedJobRef already prevents re-storms, so depending on
  // `result` here is correct and sufficient.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  // Download handler for the selected file
  const handleDownload = useCallback(async () => {
    if (!selectedFile || selectedFile.isInput) return;
    try {
      const blob = await executeApi.getOutputFileBlob(selectedFile.url);
      triggerBlobDownload(blob, selectedFile.name);
    } catch (err) {
      setViewerError(err instanceof Error ? err.message : "Download failed");
    }
  }, [selectedFile]);

  // Whether the viewer toolbar download button is shown (xlsx/other place their
  // own Download button inside the placeholder card, so we skip the toolbar one).
  const showToolbarDownload = !!selectedFile && !selectedFile.isInput
    && selectedFile.type !== "xlsx" && selectedFile.type !== "other";

  return (
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden", flexDirection: "column" }}>

      {/* ── Main layout ──────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
        {/* ── Left Sidebar (file list) ─────────────────────────── */}
        <Box
          sx={{
            width: LEFT_SIDEBAR_WIDTH,
            minWidth: LEFT_SIDEBAR_WIDTH,
            flexShrink: 0,
            borderRight: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
          }}
        >
          <List dense sx={{ flex: 1, overflow: "auto", py: 0.5 }}>
            {outputEntries.length === 0 ? (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="caption" color="text.disabled">
                  No output files
                </Typography>
              </Box>
            ) : (
              outputEntries.map((file, idx) => {
                const isFirstOutput = !file.isInput && (idx === 0 || outputEntries[idx - 1].isInput);
                const isFirstInput = file.isInput && idx === 0;
                const Icon = iconForType(file.type);
                return (
                  <Box key={file.url}>
                    {/* Section label above first input entry */}
                    {isFirstInput && (
                      <Typography
                        variant="overline"
                        color="text.disabled"
                        sx={{ pt: 0.75, pb: 0.25, display: "block", fontSize: "0.6rem", lineHeight: 1.5, textAlign: "center" }}
                      >
                        Input
                      </Typography>
                    )}
                    {/* Section label + border separator above first output entry */}
                    {isFirstOutput && idx > 0 && (
                      <Box sx={{ mt: 0.5, borderTop: "1px solid", borderColor: "divider", pt: 1, pb: 0.25, textAlign: "center" }}>
                        <Typography
                          variant="overline"
                          color="text.disabled"
                          sx={{ fontSize: "0.6rem", lineHeight: 1.5 }}
                        >
                          Output
                        </Typography>
                      </Box>
                    )}
                    {isFirstOutput && idx === 0 && (
                      <Typography
                        variant="overline"
                        color="text.disabled"
                        sx={{ pt: 0.75, pb: 0.25, display: "block", fontSize: "0.6rem", lineHeight: 1.5, textAlign: "center" }}
                      >
                        Output
                      </Typography>
                    )}
                    <Tooltip title={file.name} placement="right">
                      <ListItemButton
                        selected={selectedFile?.url === file.url}
                        onClick={() => setSelectedFile(file)}
                        sx={{ py: 0.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <Icon
                            sx={{ fontSize: 16, color: file.isInput ? "primary.main" : "text.secondary" }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={file.size_bytes != null ? formatBytes(file.size_bytes) : null}
                          primaryTypographyProps={{
                            variant: "caption",
                            noWrap: true,
                            color: file.isInput ? "primary.main" : "inherit",
                          }}
                          secondaryTypographyProps={{
                            variant: "caption",
                            sx: { fontSize: "0.65rem", lineHeight: 1.2 },
                          }}
                        />
                      </ListItemButton>
                    </Tooltip>
                  </Box>
                );
              })
            )}
          </List>
        </Box>

        {/* ── Center: Viewer + Console ──────────────────── */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            minWidth: 0,
          }}
        >
          {/* Type-aware viewer */}
          <Box sx={{ flex: 1, position: "relative", overflow: "hidden", display: "flex", minHeight: 0 }}>
            {!selectedFile ? (
              <EmptyViewerState />
            ) : viewerLoading ? (
              <CenteredSpinner />
            ) : viewerError ? (
              <ErrorState message={viewerError} />
            ) : selectedFile.type === "image" ? (
              <ImageViewer
                viewerRef={viewerRef}
                imageUrl={imageUrl}
                imageName={selectedFile.name}
                zoom={zoom}
                translateX={translateX}
                translateY={translateY}
                isPanning={isPanning}
                onWheel={handleViewerWheel}
                onMouseDown={handleViewerMouseDown}
                onMouseMove={handleViewerMouseMove}
                onMouseUp={handleViewerMouseUp}
                onDoubleClick={resetView}
                onOpenFullscreen={() => setShowImageModal(true)}
                overlay={overlayVisible ? selectedFile.overlay : undefined}
              />
            ) : selectedFile.type === "pdf" ? (
              <PdfViewer url={pdfUrl} filename={selectedFile.name} />
            ) : selectedFile.type === "csv" ? (
              <CsvViewer
                rows={csvRows}
                rawText={csvRawText}
                showRaw={showCsvRaw}
                onToggleRaw={() => setShowCsvRaw((s) => !s)}
              />
            ) : selectedFile.type === "text" ? (
              <TextViewer content={textContent} />
            ) : (
              <DownloadPlaceholder
                file={selectedFile}
                onDownload={handleDownload}
              />
            )}

            {/* Viewer toolbar — top-right overlay, shown for most file types */}
            {selectedFile && !selectedFile.isInput && !viewerLoading && !viewerError && (
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  display: "flex",
                  gap: 0.5,
                  zIndex: 2,
                }}
              >
                {showToolbarDownload && (
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={handleDownload}
                      aria-label="Download file"
                      sx={{
                        bgcolor: "rgba(0,0,0,0.5)",
                        color: "rgba(255,255,255,0.9)",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                      }}
                    >
                      <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {selectedFile.type === "image" && selectedFile.overlay && selectedFile.overlay.shapes.length > 0 && (
                  <Tooltip title={overlayVisible ? "Hide overlay" : "Show overlay"}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setOverlayVisible((v) => !v)}
                      aria-label="Toggle overlay"
                      startIcon={
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: overlayVisible ? DEFAULT_OVERLAY_STROKE : "transparent",
                            border: `2px solid ${DEFAULT_OVERLAY_STROKE}`,
                          }}
                        />
                      }
                      sx={{
                        height: 28,
                        minWidth: 0,
                        px: 1,
                        textTransform: "none",
                        fontSize: "0.7rem",
                        bgcolor: "rgba(0,0,0,0.5)",
                        color: "rgba(255,255,255,0.9)",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                      }}
                    >
                      Overlay
                    </Button>
                  </Tooltip>
                )}
                {selectedFile.type === "image" && imageUrl && (
                  <Tooltip title="View fullscreen">
                    <IconButton
                      size="small"
                      onClick={() => setShowImageModal(true)}
                      aria-label="View fullscreen"
                      sx={{
                        bgcolor: "rgba(0,0,0,0.5)",
                        color: "rgba(255,255,255,0.9)",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                      }}
                    >
                      <OpenInFullOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}

            {/* Overlay legend — bottom-left, inside viewer */}
            {selectedFile?.type === "image"
              && overlayVisible
              && selectedFile.overlay
              && selectedFile.overlay.shapes.some((s) => s.label)
              && (
                <OverlayLegend shapes={selectedFile.overlay.shapes} />
              )}
          </Box>

          {/* Console panel — shared component, identical UX in PythonTab editor view */}
          <ConsolePanel result={result} />
        </Box>

        {/* ── AI Panel ─────────────────────────────────────────── */}
        <AiPanel
          containerWidth={containerWidth - LEFT_SIDEBAR_WIDTH}
          enableSnap={false}
          initialWidth={360}
          messages={chatMessages}
          onMessagesChange={onChatMessagesChange}
          onRunScript={onRun}
          isRunning={isRunning}
          autoAnalyzeError={autoAnalyzeError}
          onAutoAnalyzeErrorConsumed={onAutoAnalyzeErrorConsumed}
          getEditorCode={getEditorCode}
          onInjectCode={onInjectCode}
          onRestoreCode={onRestoreCode}
          marieSessionId={marieSessionId}
          onMarieSessionIdChange={onMarieSessionIdChange}
          newSessionArmKey={newSessionArmKey}
          onArmNewSession={onArmNewSession}
          onClearWorkspace={onClearWorkspace}
          allowedModels={allowedModels}
          onTierLimitToast={onTierLimitToast}
          selectedModel={selectedMarieModel}
          onSelectedModelChange={onSelectedMarieModelChange}
        />
      </Box>

      {/* ── Modals ───────────────────────────────────────────── */}
      <FullscreenImageModal
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        imageName={selectedFile?.name}
        blobUrl={imageUrl ?? undefined}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────
// Viewer sub-components
// ─────────────────────────────────────────────────────────────

function EmptyViewerState() {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 64, color: "text.disabled", display: "block", mx: "auto", mb: 1 }} />
        <Typography variant="body2" color="text.disabled">
          No file selected
        </Typography>
      </Box>
    </Box>
  );
}

function CenteredSpinner() {
  return (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "background.default" }}>
      <CircularProgress size={40} />
    </Box>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "background.default" }}>
      <Box sx={{ textAlign: "center", maxWidth: 400, p: 3 }}>
        <ErrorOutlineIcon sx={{ fontSize: 40, color: "error.main", display: "block", mx: "auto", mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      </Box>
    </Box>
  );
}

interface ImageViewerProps {
  viewerRef: React.RefObject<HTMLDivElement>;
  imageUrl: string | null;
  imageName: string;
  zoom: number;
  translateX: number;
  translateY: number;
  isPanning: boolean;
  onWheel: (e: React.WheelEvent<HTMLDivElement>) => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onDoubleClick: () => void;
  onOpenFullscreen: () => void;
  overlay?: ImageOverlay;
  onImageLoaded?: (naturalWidth: number, naturalHeight: number) => void;
}

function ImageViewer(props: ImageViewerProps) {
  const {
    viewerRef, imageUrl, imageName, zoom, translateX, translateY, isPanning,
    onWheel, onMouseDown, onMouseMove, onMouseUp, onDoubleClick,
    overlay, onImageLoaded,
  } = props;
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  return (
    <Box
      ref={viewerRef}
      onWheel={onWheel}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onDoubleClick={onDoubleClick}
      sx={{
        flex: 1,
        overflow: "hidden",
        backgroundImage: `
          linear-gradient(45deg, #666 25%, transparent 25%),
          linear-gradient(-45deg, #666 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, #666 75%),
          linear-gradient(-45deg, transparent 75%, #666 75%)
        `,
        backgroundSize: "16px 16px",
        backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
        backgroundColor: "#444",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: isPanning ? "grabbing" : "grab",
        position: "relative",
        userSelect: "none",
      }}
    >
      {imageUrl ? (
        <Box
          sx={{
            position: "relative",
            display: "inline-block",
            maxWidth: "100%",
            maxHeight: "100%",
            transform: `translate(${translateX}px, ${translateY}px) scale(${zoom})`,
            transition: isPanning ? "none" : "transform 80ms",
            transformOrigin: "center",
            pointerEvents: "none",
          }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt={imageName}
            draggable={false}
            onLoad={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              const w = img.naturalWidth;
              const h = img.naturalHeight;
              setNatural({ w, h });
              onImageLoaded?.(w, h);
            }}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              display: "block",
              pointerEvents: "none",
            }}
          />
          {overlay && natural && (
            <svg
              viewBox={`0 0 ${natural.w} ${natural.h}`}
              preserveAspectRatio="xMidYMid meet"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            >
              {overlay.shapes.map((s, i) => (
                <OverlayShapeEl key={i} shape={s} />
              ))}
            </svg>
          )}
        </Box>
      ) : (
        <ImageOutlinedIcon sx={{ fontSize: 64, color: "rgba(255,255,255,0.15)" }} />
      )}
    </Box>
  );
}

function OverlayShapeEl({ shape }: { shape: OverlayShape }) {
  const stroke = shapeStroke(shape);
  const strokeWidth = shape.stroke_width ?? DEFAULT_OVERLAY_STROKE_WIDTH;
  const fill = shape.fill ?? "none";
  const dash = shape.stroke_dash;
  const common = {
    stroke,
    strokeWidth,
    fill,
    ...(dash ? { strokeDasharray: dash } : {}),
    vectorEffect: "non-scaling-stroke" as const,
  };

  switch (shape.type) {
    case "rectangle": {
      const rot = shape.rotation ?? 0;
      const cx = shape.x + shape.width / 2;
      const cy = shape.y + shape.height / 2;
      return (
        <rect
          x={shape.x}
          y={shape.y}
          width={shape.width}
          height={shape.height}
          transform={rot ? `rotate(${rot} ${cx} ${cy})` : undefined}
          {...common}
        />
      );
    }
    case "ellipse": {
      const rot = shape.rotation ?? 0;
      return (
        <ellipse
          cx={shape.cx}
          cy={shape.cy}
          rx={shape.rx}
          ry={shape.ry}
          transform={rot ? `rotate(${rot} ${shape.cx} ${shape.cy})` : undefined}
          {...common}
        />
      );
    }
    case "point": {
      return (
        <circle
          cx={shape.x}
          cy={shape.y}
          r={5}
          fill={stroke}
          stroke="none"
        />
      );
    }
    case "polyline": {
      const pts = shape.points.map(([x, y]) => `${x},${y}`).join(" ");
      if (shape.closed) {
        return <polygon points={pts} {...common} />;
      }
      return <polyline points={pts} {...{ ...common, fill: "none" }} />;
    }
    default:
      return null;
  }
}

function OverlayLegend({ shapes }: { shapes: OverlayShape[] }) {
  // Build unique label -> color map, preserving first-seen order.
  const entries: Array<{ label: string; color: string }> = [];
  const seen = new Set<string>();
  for (const s of shapes) {
    if (!s.label) continue;
    if (seen.has(s.label)) continue;
    seen.add(s.label);
    entries.push({ label: s.label, color: shapeStroke(s) });
  }
  if (entries.length === 0) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 8,
        left: 8,
        zIndex: 2,
        bgcolor: "rgba(0,0,0,0.6)",
        color: "rgba(255,255,255,0.95)",
        borderRadius: 1,
        px: 1,
        py: 0.75,
        maxWidth: 240,
        fontSize: "0.7rem",
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
      }}
    >
      {entries.map((e) => (
        <Box key={e.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "2px",
              bgcolor: e.color,
              flexShrink: 0,
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          />
          <Typography component="span" sx={{ fontSize: "0.7rem", lineHeight: 1.3 }}>
            {e.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function PdfViewer({ url, filename }: { url: string | null; filename: string }) {
  if (!url) return <CenteredSpinner />;
  return (
    <Box sx={{ flex: 1, bgcolor: "background.default", display: "flex", minHeight: 0 }}>
      <iframe
        src={url}
        title={filename}
        style={{ border: 0, width: "100%", height: "100%" }}
      />
    </Box>
  );
}

interface CsvViewerProps {
  rows: string[][] | null;
  rawText: string | null;
  showRaw: boolean;
  onToggleRaw: () => void;
}

function CsvViewer({ rows, rawText, showRaw, onToggleRaw }: CsvViewerProps) {
  if (!rows || rows.length === 0) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "background.default" }}>
        <Typography variant="body2" color="text.disabled">Empty CSV file</Typography>
      </Box>
    );
  }

  const headers = rows[0];
  const dataRows = rows.slice(1);
  const displayRows = dataRows.slice(0, CSV_ROW_CAP);
  const truncated = dataRows.length > CSV_ROW_CAP;

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "background.default", minHeight: 0 }}>
      {/* Toolbar row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        {truncated && !showRaw && (
          <Chip
            size="small"
            label={`Showing first ${CSV_ROW_CAP.toLocaleString()} of ${dataRows.length.toLocaleString()} rows`}
            sx={{ height: 22, fontSize: "0.7rem" }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="text"
          onClick={onToggleRaw}
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          {showRaw ? "View as table" : "View raw"}
        </Button>
      </Box>

      {showRaw ? (
        <Box
          component="pre"
          sx={{
            flex: 1,
            m: 0,
            overflow: "auto",
            p: 1.5,
            fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
            fontSize: "0.75rem",
            lineHeight: 1.5,
            whiteSpace: "pre",
            color: "text.primary",
            bgcolor: "background.default",
          }}
        >
          {rawText ?? ""}
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {headers.map((h, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      bgcolor: "background.paper",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row, ri) => (
                <TableRow key={ri} hover>
                  {headers.map((_, ci) => (
                    <TableCell
                      key={ci}
                      sx={{
                        fontSize: "0.75rem",
                        fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row[ci] ?? ""}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function TextViewer({ content }: { content: string | null }) {
  return (
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        bgcolor: "background.default",
        minHeight: 0,
      }}
    >
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
          fontSize: "0.8rem",
          lineHeight: 1.6,
          color: "text.primary",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {content ?? ""}
      </Box>
    </Box>
  );
}

interface DownloadPlaceholderProps {
  file: OutputFile;
  onDownload: () => void;
}

function DownloadPlaceholder({ file, onDownload }: DownloadPlaceholderProps) {
  const Icon = iconForType(file.type);
  const isXlsx = file.type === "xlsx";
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          maxWidth: 420,
          width: "100%",
          p: 4,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Icon sx={{ fontSize: 72, color: "primary.main" }} />
        <Typography variant="h6" sx={{ wordBreak: "break-all" }}>
          {file.name}
        </Typography>
        {file.size_bytes != null && (
          <Typography variant="body2" color="text.secondary">
            {formatBytes(file.size_bytes)}
          </Typography>
        )}
        <Button
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          onClick={onDownload}
          sx={{ mt: 1 }}
        >
          Download
        </Button>
        {isXlsx && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
            Open in Excel or another spreadsheet application.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
